import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileStack, Save, ExternalLink, RotateCcw } from "lucide-react";

export const VersionHistoryPanel = ({
  jobId,
  snapshots,
  readOnlyVersion,
  savingVersion,
  onSaveVersion,
  onRevertVersion,
  onViewVersion,
  formatCurrency,
}) => {
  if (!jobId || !snapshots || snapshots.length === 0) return null;

  return (
    <div
      className="bg-white rounded-lg border border-indigo-200 shadow-sm"
      data-testid="version-history-panel"
    >
      <div className="px-5 py-4 flex items-center justify-between border-b border-indigo-100">
        <h3 className="text-sm font-semibold text-indigo-800 flex items-center gap-2">
          <FileStack className="w-4 h-4 text-indigo-500" />
          Version History
          <Badge variant="outline" className="text-xs border-indigo-200 text-indigo-700">
            {snapshots.length} {snapshots.length === 1 ? "version" : "versions"}
          </Badge>
        </h3>
        {!readOnlyVersion && (
          <Button
            size="sm"
            variant="outline"
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            onClick={onSaveVersion}
            disabled={savingVersion}
            data-testid="save-version-btn"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {savingVersion ? "Saving…" : "Save current as version"}
          </Button>
        )}
      </div>
      <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
        {[...snapshots].reverse().map((snap) => {
          const isLatest = snap.version === snapshots.length;
          const isViewing = readOnlyVersion === snap.version;
          const dateStr = snap.timestamp || snap.created_at;
          return (
            <div
              key={snap.version}
              className={`px-5 py-3 flex items-center justify-between gap-3 ${
                isViewing ? "bg-amber-50" : "hover:bg-gray-50/60"
              }`}
              data-testid={`version-row-${snap.version}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${isLatest ? "bg-indigo-600" : "bg-slate-400"}`}>
                    V{snap.version}
                  </Badge>
                  {isLatest && (
                    <span className="text-xs font-medium text-indigo-700">Current</span>
                  )}
                  {snap.status_at_snapshot && (
                    <span className="text-xs text-slate-500">{snap.status_at_snapshot}</span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {dateStr ? new Date(dateStr).toLocaleString("en-AU", {
                    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                  }) : "—"}
                  {typeof snap.total_inc_gst === "number" && (
                    <span className="font-mono text-slate-700"> · {formatCurrency(snap.total_inc_gst)} inc GST</span>
                  )}
                </p>
                {snap.change_note && (
                  <p className="text-xs text-slate-600 italic mt-0.5 truncate">“{snap.change_note}”</p>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-slate-600 hover:text-indigo-700 h-8 px-2"
                  onClick={() => onViewVersion(snap.version)}
                  title="Open this version in a new tab"
                  data-testid={`view-version-${snap.version}`}
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
                {!isLatest && !readOnlyVersion && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-300 text-amber-800 hover:bg-amber-50 h-8 px-2"
                    onClick={() => onRevertVersion(snap.version)}
                    title={`Revert to V${snap.version}`}
                    data-testid={`revert-version-${snap.version}`}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
